/**
 * VendorGmapsReviews — public-facing section that renders the vendor's
 * imported Google Maps reviews on their detail page. Fetches from
 * `/api/vendor/:id/gmaps-reviews`; renders nothing if no reviews are
 * imported yet (silent).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, GoogleLogo } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ value }) => {
  const filled = Math.round(value || 0);
  return (
    <span className="inline-flex items-center" data-testid="gmaps-review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={14}
          weight={n <= filled ? 'fill' : 'regular'}
          className={n <= filled ? 'text-amber-500' : 'text-gray-300'}
        />
      ))}
    </span>
  );
};

const VendorGmapsReviews = ({ vendorId }) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!vendorId) return undefined;
    let mounted = true;
    axios
      .get(`${BACKEND_URL}/api/vendor/${vendorId}/gmaps-reviews`)
      .then((res) => {
        if (!mounted) return;
        setReviews(res.data?.reviews || []);
        setSummary(res.data?.summary || null);
      })
      .catch(() => { /* silent — vendor hasn't imported yet */ });
    return () => { mounted = false; };
  }, [vendorId]);

  if (!reviews.length) return null;

  return (
    <section className="mt-8 border-2 border-[#E8E1D5] bg-white rounded p-5"
      data-testid="vendor-gmaps-reviews-section">
      <header className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-heading text-xl flex items-center gap-2">
          <GoogleLogo size={20} weight="fill" className="text-[#4285F4]" />
          Google Reviews
        </h2>
        {summary && (
          <div className="text-sm text-text-secondary flex items-center gap-2"
            data-testid="vendor-gmaps-summary">
            <Stars value={summary.rating} />
            <span className="font-bold text-text-primary">
              {Number(summary.rating || 0).toFixed(1)}
            </span>
            <span>· {summary.count?.toLocaleString?.() || summary.count} reviews on Google</span>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reviews.map((r, idx) => (
          <article
            key={`${r.author}-${idx}`}
            className="border border-[#E8E1D5] rounded p-3 bg-[#FAF6EE]/50"
            data-testid={`vendor-gmaps-review-${idx}`}
          >
            <div className="flex items-start gap-3 mb-2">
              {r.author_photo ? (
                <img
                  src={r.author_photo}
                  alt={r.author}
                  referrerPolicy="no-referrer"
                  className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-[#E8E1D5] flex items-center justify-center text-sm font-bold text-text-secondary flex-shrink-0">
                  {(r.author || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{r.author || 'Anonymous'}</div>
                <div className="flex items-center gap-2 text-[11px] text-text-secondary">
                  <Stars value={r.rating} />
                  {r.relative_time && <span>· {r.relative_time}</span>}
                </div>
              </div>
            </div>
            {r.text && (
              <p className="text-sm whitespace-pre-line line-clamp-6">
                {r.text}
              </p>
            )}
          </article>
        ))}
      </div>

      {summary?.place_name && (
        <p className="text-[11px] text-text-secondary mt-3">
          Imported from Google listing: <strong>{summary.place_name}</strong>
        </p>
      )}
    </section>
  );
};

export default VendorGmapsReviews;
